import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    MenuItem,
    Select,
    InputLabel,
    Chip,
    CircularProgress,
} from '@mui/material';
import {
    MESSAGES,
    EMPTY_OVERRIDE,
    hasChanges,
    sendToFrame,
    subscribe,
} from '../../../ultis/lumenPanelProtocol';

// how long to wait for the cache page to answer before assuming it has no listener
const REPLY_TIMEOUT_MS = 2500;
const OFFSET_STEP = 8;

const MUTED = 'rgb(99, 115, 129)';

function ElementSummary({ element }) {
    // id is the most useful identifier when present, class next, tag as a fallback
    const primary = element.id || element.className || element.tag;

    return (
        <ListItemText
            primary={primary}
            secondary={element.text || `<${element.tag}>`}
            primaryTypographyProps={{ fontSize: 14, noWrap: true }}
            secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
        />
    );
}

export default function LumenElementPanel({ open, onClose, frameRef, overrides, applyLumenOverrides }) {

    const [elements, setElements] = useState([])
    const [selectedKey, setSelectedKey] = useState(null)
    const [status, setStatus] = useState('idle')
    const timeoutRef = useRef(null)
    // 'idle' | 'waiting' | 'ok' | 'silent' - result of the manual connection test
    const [pingState, setPingState] = useState('idle')
    const pingTimeoutRef = useRef(null)

    /*
     * Selection is tracked by element.key, NOT element.id.
     *
     * `key` is the identifier the cache page minted and is keyed by in its own registry
     * ("id:lumen-header", "class:lumen-slot#0"). `id` is just the raw DOM id, so:
     *
     *   - an override keyed "lumen-header" never matches the page's "id:lumen-header",
     *     and the edit is dropped without an error
     *   - every element without an id reports id "", so they all collide on one key and
     *     editing any of them would target all of them
     */
    const selected = elements.find((element) => element.key === selectedKey) || null
    const override = (selectedKey && overrides[selectedKey]) || EMPTY_OVERRIDE


    // listen for whatever the cache page sends back
    useEffect(() => {
        const unsubscribe = subscribe((message) => {
            if (message.name === MESSAGES.ELEMENTS) {
                clearTimeout(timeoutRef.current)
                // create structure
                setElements(Array.isArray(message.value) ? message.value : [])
                setStatus('ready')
            }

            // the page announcing itself, either unprompted on load or answering a ping
            if (message.name === MESSAGES.READY) {
                clearTimeout(pingTimeoutRef.current)
                setPingState('ok')
            }
        })

        return unsubscribe
    }, [])

    // A deliberate poke at the cache page. One click, one message, one log line at each end -
    // the quickest way to tell whether the channel is open before blaming the controls.
    function testConnection() {
        setPingState('waiting')

        const sent = sendToFrame(frameRef.current, MESSAGES.PING)

        if (!sent) {
            setPingState('silent')
            return
        }

        clearTimeout(pingTimeoutRef.current)
        pingTimeoutRef.current = setTimeout(() => setPingState('silent'), REPLY_TIMEOUT_MS)
    }

    useEffect(() => () => clearTimeout(pingTimeoutRef.current), [])

    // ask for the element list each time the panel opens, so it reflects the page as it is now
    useEffect(() => {
        if (!open) {
            return undefined
        }

        setStatus('waiting')
        setElements([])
        setSelectedKey(null)

        const sent = sendToFrame(frameRef.current, MESSAGES.LIST_ELEMENTS)

        if (!sent) {
            setStatus('unreachable')
            return undefined
        }

        // A cache page without the listener never replies. Say so plainly rather than
        // leaving an empty list that looks like "this page has no lumen elements".
        timeoutRef.current = setTimeout(() => setStatus('no-listener'), REPLY_TIMEOUT_MS)

        return () => clearTimeout(timeoutRef.current)
    }, [open, frameRef])

    // outline the selected element in the page so it is obvious which one is being edited
    useEffect(() => {
        if (!open) {
            return
        }

        sendToFrame(frameRef.current, MESSAGES.HIGHLIGHT, { key: selectedKey })
    }, [open, selectedKey, frameRef])

    // clear the outline on close, otherwise it is left drawn on the page
    useEffect(() => {
        if (open) {
            return
        }

        sendToFrame(frameRef.current, MESSAGES.HIGHLIGHT, { key: null })
    }, [open, frameRef])

    function updateSelected(changes) {
        if (!selectedKey) {
            return
        }

        const next = { ...overrides, [selectedKey]: { ...override, ...changes } }

        applyLumenOverrides(next)
    }

    function resetSelected() {
        if (!selectedKey) {
            return
        }

        const next = { ...overrides }

        delete next[selectedKey]
        applyLumenOverrides(next)
    }

    function resetAll() {
        applyLumenOverrides({})
    }

    const changedCount = Object.keys(overrides).filter((key) => hasChanges(overrides[key])).length

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
                Page elements
                {changedCount ? (
                    <Chip size="small" label={`${changedCount} changed`} />
                ) : null}
            </DialogTitle>

            <DialogContent dividers>
                {status === 'waiting' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '24px 0' }}>
                        <CircularProgress size={18} />
                        <span style={{ fontSize: 14, color: MUTED }}>reading the cache page...</span>
                    </div>
                ) : null}

                {status === 'unreachable' ? (
                    <p style={{ fontSize: 14, margin: '16px 0' }}>
                        The cache page preview is not loaded yet. Close this, wait for the page to
                        appear, then try again.
                    </p>
                ) : null}

                {status === 'no-listener' ? (
                    <div style={{ fontSize: 14, margin: '16px 0' }}>
                        <p style={{ margin: '0 0 8px' }}>
                            This cache page did not respond, so its elements cannot be listed.
                        </p>
                        <p style={{ margin: 0, color: MUTED }}>
                            The page is served from another domain, so it has to opt in to being
                            edited. Cache pages need the Lumen panel listener script for this to
                            work - pages captured before it was added will not respond.
                        </p>
                    </div>
                ) : null}

                {status === 'ready' && !elements.length ? (
                    <p style={{ fontSize: 14, margin: '16px 0' }}>
                        No elements starting with &quot;lumen&quot; were found on this page.
                    </p>
                ) : null}

                {status === 'ready' && elements.length ? (
                    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

                        <div style={{ width: 260, flexShrink: 0, maxHeight: 360, overflow: 'auto', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6 }}>
                            <List dense disablePadding>
                                {elements.map((element) => (
                                    <ListItemButton
                                        key={element.key}
                                        selected={element.key === selectedKey}
                                        onClick={() => setSelectedKey(element.key)}
                                    >
                                        <ElementSummary element={element} />
                                        {hasChanges(overrides[element.key]) ? (
                                            <Chip size="small" label="edited" sx={{ ml: 1 }} />
                                        ) : null}
                                    </ListItemButton>
                                ))}
                            </List>
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                            {!selected ? (
                                <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
                                    Select an element to change how it looks.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                                            {selected.id || selected.className || selected.tag}
                                        </div>
                                        <div style={{ fontSize: 12, color: MUTED }}>
                                            &lt;{selected.tag}&gt; &middot; currently {selected.position}
                                        </div>
                                    </div>

                                    <Divider />

                                    {/* position ---------------------------------------------- */}
                                    <div>
                                        <InputLabel sx={{ fontSize: 12, mb: 0.5 }}>Position</InputLabel>
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={override.position || ''}
                                            onChange={(event) => updateSelected({ position: event.target.value })}
                                        >
                                            <MenuItem value="">leave as the page has it ({selected.position})</MenuItem>
                                            <MenuItem value="static">static &mdash; stops it sticking</MenuItem>
                                            <MenuItem value="fixed">fixed &mdash; pinned to the viewport</MenuItem>
                                            <MenuItem value="relative">relative</MenuItem>
                                            <MenuItem value="absolute">absolute</MenuItem>
                                        </Select>
                                    </div>

                                    {/* horizontal nudge -------------------------------------- */}
                                    <div>
                                        <InputLabel sx={{ fontSize: 12, mb: 0.5 }}>
                                            Horizontal position ({override.offsetX || 0}px)
                                        </InputLabel>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => updateSelected({ offsetX: (override.offsetX || 0) - OFFSET_STEP })}
                                            >
                                                move left
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => updateSelected({ offsetX: (override.offsetX || 0) + OFFSET_STEP })}
                                            >
                                                move right
                                            </Button>
                                            {override.offsetX ? (
                                                <Button size="small" onClick={() => updateSelected({ offsetX: 0 })}>
                                                    centre
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>

                                    {/* colours ----------------------------------------------- */}
                                    <div style={{ display: 'flex', gap: 24 }}>
                                        <div>
                                            <InputLabel sx={{ fontSize: 12, mb: 0.5 }}>Text colour</InputLabel>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <input
                                                    type="color"
                                                    value={override.color || '#000000'}
                                                    onChange={(event) => updateSelected({ color: event.target.value })}
                                                    style={{ width: 44, height: 32, padding: 0, border: '1px solid rgba(0,0,0,0.2)', borderRadius: 4, background: 'none' }}
                                                />
                                                {override.color ? (
                                                    <Button size="small" onClick={() => updateSelected({ color: '' })}>clear</Button>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div>
                                            <InputLabel sx={{ fontSize: 12, mb: 0.5 }}>Background</InputLabel>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                <input
                                                    type="color"
                                                    value={override.backgroundColor || '#ffffff'}
                                                    onChange={(event) => updateSelected({ backgroundColor: event.target.value })}
                                                    style={{ width: 44, height: 32, padding: 0, border: '1px solid rgba(0,0,0,0.2)', borderRadius: 4, background: 'none' }}
                                                />
                                                {override.backgroundColor ? (
                                                    <Button size="small" onClick={() => updateSelected({ backgroundColor: '' })}>clear</Button>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>

                                    <Divider />

                                    {/* remove ------------------------------------------------ */}
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        {override.hidden ? (
                                            <Button size="small" variant="outlined" onClick={() => updateSelected({ hidden: false })}>
                                                put the element back
                                            </Button>
                                        ) : (
                                            <Button size="small" variant="outlined" color="error" onClick={() => updateSelected({ hidden: true })}>
                                                remove the element
                                            </Button>
                                        )}

                                        {hasChanges(overrides[selectedKey]) ? (
                                            <Button size="small" onClick={resetSelected}>reset this element</Button>
                                        ) : null}
                                    </div>

                                    {override.hidden ? (
                                        <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>
                                            Hidden from the page. The other changes above are kept and
                                            take effect again if you put it back.
                                        </p>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
                {/* fires one message on click, so the console at each end shows a matching
                    line - the fastest way to prove the channel before blaming the controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button size="small" onClick={testConnection}>test connection</Button>

                    {pingState === 'waiting' ? (
                        <span style={{ fontSize: 12, color: MUTED }}>waiting for the page...</span>
                    ) : null}
                    {pingState === 'ok' ? (
                        <span style={{ fontSize: 12, color: '#00ab55' }}>the cache page answered</span>
                    ) : null}
                    {pingState === 'silent' ? (
                        <span style={{ fontSize: 12, color: '#b72136' }}>
                            no answer - the listener is not on this page
                        </span>
                    ) : null}
                </div>

                <div>
                    {changedCount ? (
                        <Button onClick={resetAll}>reset all</Button>
                    ) : null}
                    <Button className="button-primary" onClick={onClose}>done</Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
